import { GatewayError } from './customErrorTypes';

const errorFactories = {
  /* could not find HTMLElement to insert Iframe */
  INVALID_ELEMENTORSELECTOR: () =>
    new GatewayError('invalid_elementorselector', { capture: false }),

  /* gateway_name passed in the sdk initialization is invalid or not registered */
  INVALID_GATEWAY: () => new GatewayError('invalid_gateway', { capture: false }),

  /* smallcaseAuthToken is not provided or is invalid */
  INVALID_USER: () => new GatewayError('invalid_user_data', { capture: false }),

  INIT_FAILED: () => new GatewayError('invalid_user_session'),

  /* a smallcase could not be found with given information */
  INVALID_SCID: () => new GatewayError('invalid_scid', { capture: false }),

  /* cannot find the invested smallcase */
  INVALID_ISCID: () => new GatewayError('invalid_iscid', { capture: false }),

  /* transactionId passed to the sdk is incorrect */
  INVALID_TRANSACTION: () =>
    new GatewayError('invalid_transaction', { capture: false }),

  NOT_AUTHORIZED: () => new GatewayError('not_authorized', { capture: false }),

  NOT_CONNECTED: () => new GatewayError('user_not_connected', { capture: false }),

  NOT_INVESTED: () => new GatewayError('not_invested', { capture: false }),

  INVALID_USER_SESSION: () => new GatewayError('invalid_user_session'),

  /* transaction session expired */
  TIMED_OUT: () => new GatewayError('timed_out', { code: 4003 }),

  /* something went wrong while trying to place the order */
  INVALID_ORDER: () => new GatewayError('invalid_order', { code: 3002 }),

  /* user has closed the broker chooser popup */
  USER_CANCELLED: () => new GatewayError('user_cancelled', { code: 1010, capture: false }),

  /** user is expected to login with the mapped broker account only.
   * user logged in with another broker account */
  USER_MISMATCH: () => new GatewayError('user_mismatch', { code: 1001, capture: false }),

  /* broker is not accepting orders */
  MARKET_CLOSED: () => new GatewayError('market_closed', { code: 4004, capture: false }),

  /* user wants to sell a stock he does not own, order can not be placed */
  INSUFFICIENT_HOLDINGS: () =>
    new GatewayError('insufficent_holdings', { code: 1004, capture: false }),

  /* something went wrong with our APIs/SDK */
  API_ERROR: () => new GatewayError('internal_error', { code: 2000 }),

  ERROR: () => new GatewayError('api_error'),

  ORDER_IN_QUEUE: () => new GatewayError('order_in_queue', { capture: false }),

  /* user denied consent for holdings import */
  CONSENT_DENIED: () => new GatewayError('consent_denied', { code: 1003, capture: false }),

  /* transaction session expired */
  TRANSACTION_EXPIRED: () =>
    new GatewayError('transaction_expired', { code: 1005, capture: false }),

  /* user could not find his broker in the available list */
  NO_BROKER: () => new GatewayError('no_broker', { code: 1006, capture: false }),

  /** user could not find his broker in the available list
   * & opted to signup for a new broking account */
  NO_BROKER_SIGNUP: () => new GatewayError('no_broker', { code: 1007, capture: false }),

  /* selected brokers do not support the transaction intent */
  NO_BROKER_SUPPORT: () => new GatewayError('no_broker', { code: 1008, capture: false }),

  /* rebalance was ignored */
  NO_ORDER: () => new GatewayError('no_order', { code: 1020, capture: false }),

  /* no shortfall detected while reconciliation */
  NO_SHORTFALL: () => new GatewayError('no_shortfall', { code: 1021, capture: false }),

  /* last order by the user is still pending */
  ORDER_PENDING: () => new GatewayError('order_pending', { code: 4006, capture: false }),

  /* gatewayPostbackUrl could not be reached */
  WEBHOOK_UNREACHABLE: () => new GatewayError('webhook_unreachable', { code: 3003 }),

  /* user has already subscribed */
  ALREADY_SUBSCRIBED: () => new GatewayError('already_subscribed', { code: 4000, capture: false }),

  /* user has closed the order journey after selecting the broker, but before successful login */
  USER_INIT_THEN_CANCEL: () => new GatewayError('user_cancelled', { code: 1011, capture: false }),

  /* user completed broker login to view the order popup but abandoned the order journey */
  USER_LOGIN_THEN_CANCEL: () => new GatewayError('user_cancelled', { code: 1012, capture: false }),

  /* transactionId passed in the triggerTransaction method is used or expired */
  TRANSACTION_USED: () => new GatewayError('transaction_expired', { code: 3001, capture: false }),

  /* amo orders are not accepted by the broker */
  AMO_CLOSED: () => new GatewayError('market_closed', { code: 4005, capture: false }),

  /* smallcase has been deleted post archival */
  SMALLCASE_ARCHIVED: () => new GatewayError('smallcase_archived', { code: 1013, capture: false }),

  /* user opted to import holdings via account aggregator, journey continues outside the sdk */
  ACCOUNT_AGGREGATOR_FLOW_INITIATED: () =>
    new GatewayError('account_aggregator_flow_initiated', { code: 1015, capture: false }),

  /* its not possible to trade over NSE using the users account */
  TRADING_NOT_ENABLED: () =>
    new GatewayError('trading_not_enabled', { code: 1030, capture: false }),
};

type ErrorMap = {
  readonly [K in keyof typeof errorFactories]: ReturnType<
    typeof errorFactories[K]
  >;
};

/** Each access returns a new error: captures are deduped per error object. */
const errorMap = {} as ErrorMap;
Object.keys(errorFactories).forEach((key) => {
  Object.defineProperty(errorMap, key, {
    get: errorFactories[key],
    enumerable: true,
  });
});

export type SdkErrorStruct = {
  code: number;
  message: string;
  error: GatewayError;
};

const apiError = (
  code: number,
  message: string,
  key: keyof ErrorMap,
): SdkErrorStruct => ({
  code,
  message,
  get error() {
    return errorMap[key];
  },
});

export const apiErrorCodeMap: { [key: number]: SdkErrorStruct } = {
  1001: apiError(1001, 'user_mismatch', 'USER_MISMATCH'),
  1003: apiError(1003, 'consent_denied', 'CONSENT_DENIED'),
  1004: apiError(1004, 'insufficient_holdings', 'INSUFFICIENT_HOLDINGS'),
  1005: apiError(1005, 'transaction_expired', 'TRANSACTION_EXPIRED'),
  1006: apiError(1006, 'no_broker', 'NO_BROKER'),
  1007: apiError(1007, 'no_broker', 'NO_BROKER_SIGNUP'),
  1008: apiError(1008, 'no_broker', 'NO_BROKER_SUPPORT'),
  1010: apiError(1010, 'user_cancelled', 'USER_CANCELLED'),
  1011: apiError(1011, 'user_cancelled', 'USER_INIT_THEN_CANCEL'),
  1012: apiError(1012, 'user_cancelled', 'USER_LOGIN_THEN_CANCEL'),
  1013: apiError(1013, 'smallcase_archived', 'SMALLCASE_ARCHIVED'),
  1015: apiError(
    1015,
    'account_aggregator_flow_initiated',
    'ACCOUNT_AGGREGATOR_FLOW_INITIATED',
  ),
  1020: apiError(1020, 'no_order', 'NO_ORDER'),
  1021: apiError(1021, 'no_shortfall', 'NO_SHORTFALL'),
  1030: apiError(1030, 'trading_not_enabled', 'TRADING_NOT_ENABLED'),
  2000: apiError(2000, 'internal_error', 'API_ERROR'),
  3001: apiError(3001, 'transaction_expired', 'TRANSACTION_USED'),
  3002: apiError(3002, 'invalid_order', 'INVALID_ORDER'),
  3003: apiError(3003, 'webhook_unreachable', 'WEBHOOK_UNREACHABLE'),
  4000: apiError(4000, 'already_subscribed', 'ALREADY_SUBSCRIBED'),
  4003: apiError(4003, 'timed_out', 'TIMED_OUT'),
  4004: apiError(4004, 'market_closed', 'MARKET_CLOSED'),
  4005: apiError(4005, 'market_closed', 'AMO_CLOSED'),
  4006: apiError(4006, 'order_pending', 'ORDER_PENDING'),
};

export const getErrorObj = (
  code?: number,
  message?: string,
): SdkErrorStruct => {
  if (!code || !message) return apiErrorCodeMap[2000];
  if (apiErrorCodeMap[code]) return apiErrorCodeMap[code];
  return {
    code,
    message,
    error: new GatewayError(message, { code }),
  };
};
export default errorMap;
